import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  BarChart3,
  ChevronDown,
  Crown,
  Heart,
  MapPin,
  Play,
  Search,
  SlidersHorizontal,
  Sparkles,
  Target,
  Trophy,
  Users,
  X,
} from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  useCreateDirectMessageThreadMutation,
  useGetAthleteMarketplaceProfileQuery,
  useGetCampaignsQuery,
  useGetMarketplaceQuery,
  useSetShortlistedMutation,
  type MarketplaceAthlete,
} from '@/redux/marketerApi'
import { SendMessageModal, type DirectMessageData } from '@/components/athletes/SendMessageModal'
import { CONTENT_CATEGORIES } from '@adthlete/common/utils/contentCategories'

const ANY = '__any'
const SPORTS = [
  'Baseball',
  'Basketball',
  'Football',
  'Golf',
  'Gymnastics',
  'Hockey',
  'Lacrosse',
  'Soccer',
  'Softball',
  'Swimming',
  'Tennis',
  'Track & Field',
  'Volleyball',
  'Wrestling',
]
const AUDIENCE_SIZES = [
  { id: 'under_10k', label: 'Under 10K' },
  { id: '10k_100k', label: '10K – 100K' },
  { id: '100k_500k', label: '100K – 500K' },
  { id: '500k_plus', label: '500K+' },
]
const STATES =
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
    ' ',
  )

const compact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${Math.round(n / 1000)}K`
      : `${n}`
const pct = (r: number | null) => (r == null ? '—' : `${(r * 100).toFixed(1)}%`)
const platformName = (p?: string) =>
  p === 'tiktok'
    ? 'TikTok'
    : p === 'youtube'
      ? 'YouTube'
      : p
        ? p[0].toUpperCase() + p.slice(1)
        : ''
const subline = (a: MarketplaceAthlete) =>
  [a.position ?? a.sports[0], a.school].filter(Boolean).join(' · ')

/** A filter as a pill-shaped select, with an icon and an "any" choice */
const FilterSelect: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}> = ({ icon, label, value, onChange, options }) => (
  <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)}>
    <SelectTrigger
      aria-label={label}
      className={cn(
        'h-11 w-full min-w-0 gap-2 rounded-xl border-border bg-card px-3 text-sm font-medium sm:w-auto sm:min-w-[170px]',
        value && 'border-[#84CC16] bg-[#F7FEE7]',
      )}
    >
      <div className="flex min-w-0 items-center gap-2 text-foreground">
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="truncate">
          {value ? options.find((o) => o.value === value)?.label : label}
        </span>
      </div>
    </SelectTrigger>
    <SelectContent className="max-h-80">
      <SelectItem value={ANY}>Any {label.toLowerCase()}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
)

/** The fit as a ring with the score in the middle */
const FitRing: React.FC<{ score: number; size?: number }> = ({ score, size = 76 }) => {
  const r = (size - 10) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={8} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#4D7C0F"
        strokeWidth={8}
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * c} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="fill-foreground text-lg font-bold"
      >
        {score}%
      </text>
    </svg>
  )
}

const Avatar: React.FC<{ a: MarketplaceAthlete; className?: string }> = ({ a, className }) =>
  a.avatar ? (
    <img src={a.avatar} alt="" className={cn('object-cover', className)} />
  ) : (
    <span
      className={cn(
        'flex items-center justify-center bg-gradient-to-br from-[#E8F9D2] to-[#BBE79A] font-bold text-[#2F5E06]',
        className,
      )}
    >
      {a.name
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')}
    </span>
  )

/**
 * The top of a card: their content, not their headshot. The first post is big; up to two more
 * stack beside it. A theme example carries its theme, so the brand sees what they'd be buying.
 */
const CoverMosaic: React.FC<{ a: MarketplaceAthlete }> = ({ a }) => {
  const [first, ...rest] = a.covers.slice(0, 3)
  const Shot: React.FC<{ c: MarketplaceAthlete['covers'][number]; big?: boolean }> = ({
    c,
    big,
  }) => (
    <span className="relative block h-full w-full overflow-hidden bg-[#0F172A]">
      <img src={c.thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" />
      <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#000000]/60 to-transparent" />
      {c.views ? (
        <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 text-xs font-semibold text-[#FFFFFF]">
          <Play className="h-3 w-3 fill-current" /> {compact(c.views)}
        </span>
      ) : null}
      {c.theme && big ? (
        <span className="absolute bottom-2 left-12 max-w-[60%] truncate rounded-full bg-[#C0FD72] px-2 py-0.5 text-xs font-bold text-[#0F172A]">
          {c.theme}
        </span>
      ) : null}
    </span>
  )
  return (
    <span className="grid h-full w-full grid-cols-3 grid-rows-2 gap-0.5">
      <span
        className={cn('relative', rest.length ? 'col-span-2 row-span-2' : 'col-span-3 row-span-2')}
      >
        <Shot c={first} big />
      </span>
      {rest.length === 1 ? (
        <span className="row-span-2">
          <Shot c={rest[0]} />
        </span>
      ) : (
        rest.map((c) => (
          <span key={c.url}>
            <Shot c={c} />
          </span>
        ))
      )}
      {/* Who made it, small: the content leads */}
      <Avatar
        a={a}
        className="absolute bottom-2 left-2 h-9 w-9 rounded-full border-2 border-[#FFFFFF] text-xs"
      />
    </span>
  )
}

const Stats: React.FC<{ a: MarketplaceAthlete }> = ({ a }) => (
  <div className="grid grid-cols-3 divide-x divide-border text-xs">
    <div className="pr-2">
      <p className="font-bold tabular-nums text-foreground">{compact(a.followers)}</p>
      <p className="text-muted-foreground">Followers</p>
    </div>
    <div className="px-2">
      <p className="font-bold tabular-nums text-foreground">{pct(a.engagementRate)}</p>
      <p className="text-muted-foreground">Eng. rate</p>
    </div>
    <div className="pl-2">
      <p className="font-bold text-foreground">{a.state ?? '—'}</p>
      <p className="text-muted-foreground">Primary</p>
    </div>
  </div>
)

const AthleteMarketCard: React.FC<{
  a: MarketplaceAthlete
  selected: boolean
  saved: boolean
  onSelect: () => void
  onSave: () => void
  offerHref: string
  reportHref: string
}> = ({ a, selected, saved, onSelect, onSave, offerHref, reportHref }) => (
  <article
    className={cn(
      'flex min-w-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md',
      selected ? 'border-[#84CC16] ring-2 ring-[#C0FD72]' : 'border-border',
    )}
  >
    <button
      type="button"
      onClick={onSelect}
      className="relative block aspect-[4/3] w-full overflow-hidden bg-muted text-left"
      aria-label={`Show ${a.name}`}
    >
      {a.covers?.length ? (
        <CoverMosaic a={a} />
      ) : (
        <Avatar a={a} className="h-full w-full text-3xl" />
      )}
      {a.topMatch ? (
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#C0FD72] px-2.5 py-1 text-xs font-bold text-[#0F172A] shadow">
          <Crown className="h-3.5 w-3.5" /> Top match
        </span>
      ) : null}
    </button>
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <button type="button" onClick={onSelect} className="min-w-0 text-left">
          <p className="truncate text-lg font-bold text-foreground">{a.name}</p>
          <p className="truncate text-sm text-muted-foreground">{subline(a)}</p>
        </button>
        <div className="flex shrink-0 items-start gap-2">
          <div className="text-center">
            <p className="rounded-lg bg-[#E8F9D2] px-2 py-0.5 text-base font-bold tabular-nums text-[#2F5E06]">
              {a.fit.score}%
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">Campaign fit</p>
          </div>
          <button
            type="button"
            onClick={onSave}
            aria-label={saved ? `Remove ${a.name} from your shortlist` : `Save ${a.name}`}
            aria-pressed={saved}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border hover:bg-muted"
          >
            <Heart
              className={cn(
                'h-4 w-4',
                saved ? 'fill-[#E11D48] text-[#E11D48]' : 'text-muted-foreground',
              )}
            />
          </button>
        </div>
      </div>
      {/* What's for sale, then the price; the numbers come after */}
      {a.themes.length ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            In the shop
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {a.themes.slice(0, 4).map((t) => (
              <span
                key={t.label}
                className="rounded-full bg-[#F0FDE4] px-2.5 py-1 text-xs font-semibold text-[#2F5E06]"
              >
                {t.label}
              </span>
            ))}
            {a.themes.length > 4 ? (
              <span className="px-1 py-1 text-xs font-semibold text-muted-foreground">
                +{a.themes.length - 4}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {a.fromPrice ? (
          <>
            From{' '}
            <span className="text-lg font-bold text-foreground">
              ${a.fromPrice.toLocaleString()}
            </span>{' '}
            a piece · ad usage included
          </>
        ) : (
          'Prices on request'
        )}
      </p>
      <Stats a={a} />
      <div className="flex-1">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-[#3F7A0A]">
          <Sparkles className="h-4 w-4" /> Why they match
        </p>
        <p className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground">{a.fit.why}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Link
          to={reportHref}
          className="flex h-10 items-center justify-center whitespace-nowrap rounded-xl border border-border bg-card px-2 text-center text-sm font-semibold text-foreground hover:bg-muted"
        >
          Open shop
        </Link>
        <Link
          to={offerHref}
          className="flex h-10 items-center justify-center whitespace-nowrap rounded-xl bg-[#3F7A0A] px-2 text-sm font-semibold text-[#FFFFFF] hover:bg-[#356807]"
        >
          Book
        </Link>
      </div>
    </div>
  </article>
)

type Tab = 'overview' | 'audience' | 'content' | 'collabs'

/** The selected athlete, beside the grid on wide screens */
const AthletePanel: React.FC<{
  a: MarketplaceAthlete
  campaignId: string | null
  offerHref: string
  onMessage: () => void
  onClose: () => void
}> = ({ a, campaignId, offerHref, onMessage, onClose }) => {
  // Content first: what they make is the reason to book them
  const [tab, setTab] = useState<Tab>('content')
  useEffect(() => setTab('content'), [a.id])
  const { data: profile, isFetching } = useGetAthleteMarketplaceProfileQuery(
    { id: a.id, campaignId },
    { skip: tab === 'overview' },
  )
  return (
    <aside className="sticky top-4 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex gap-3 p-4">
        <Avatar a={a} className="h-20 w-20 shrink-0 rounded-xl text-xl" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-lg font-bold leading-tight text-foreground">{a.name}</p>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="-mr-1 -mt-1 rounded-md p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {a.topMatch ? (
            <span className="mt-1 inline-block rounded-full bg-[#E8F9D2] px-2 py-0.5 text-xs font-bold text-[#2F5E06]">
              Top match
            </span>
          ) : null}
          <p className="mt-1 truncate text-sm text-muted-foreground">{subline(a)}</p>
          <p className="mt-1 flex flex-wrap gap-x-3 text-xs font-semibold text-foreground">
            <span>{compact(a.followers)} followers</span>
            <span>{pct(a.engagementRate)} eng.</span>
            {a.state ? (
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="h-3 w-3" /> {a.state}
              </span>
            ) : null}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 px-4">
        <button
          type="button"
          onClick={onMessage}
          className="h-10 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted"
        >
          Message
        </button>
        <Link
          to={offerHref}
          className="flex h-10 items-center justify-center rounded-xl bg-[#3F7A0A] text-sm font-semibold text-[#FFFFFF] hover:bg-[#356807]"
        >
          Book
        </Link>
      </div>
      <div className="mt-3 flex border-b border-border px-2 text-sm" role="tablist">
        {(
          [
            ['content', 'Content'],
            ['overview', 'Fit'],
            ['audience', 'Audience'],
            ['collabs', 'Past collabs'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px flex-1 whitespace-nowrap border-b-2 px-1 py-2.5 text-sm font-semibold',
              tab === key
                ? 'border-[#4D7C0F] text-[#3F7A0A]'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3 p-4">
        {tab === 'overview' ? (
          <>
            <div className="rounded-xl border border-border p-3">
              <p className="text-sm font-bold text-foreground">Campaign fit score</p>
              <div className="mt-2 flex items-center gap-3">
                <FitRing score={a.fit.score} />
                <div className="min-w-0">
                  <p className="font-semibold text-[#3F7A0A]">{a.fit.label}</p>
                  <p className="text-xs leading-snug text-muted-foreground">{a.fit.why}</p>
                </div>
              </div>
            </div>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {a.fit.parts.map((p) => (
                <li key={p.key} className="flex items-start gap-3 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                    {p.key === 'audience' ? (
                      <Users className="h-4 w-4" />
                    ) : p.key === 'content' ? (
                      <Sparkles className="h-4 w-4" />
                    ) : p.key === 'brand' ? (
                      <Trophy className="h-4 w-4" />
                    ) : (
                      <MapPin className="h-4 w-4" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">{p.label}</p>
                      <p className="text-sm font-bold tabular-nums text-foreground">{p.score}%</p>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E5E7EB]">
                      <div
                        className="h-full rounded-full bg-[#4D7C0F]"
                        style={{ width: `${p.score}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{p.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : isFetching && !profile ? (
          <Skeleton className="h-48 w-full" />
        ) : tab === 'audience' ? (
          <>
            <div className="space-y-2">
              {a.channels.map((ch) => (
                <div
                  key={ch.platform}
                  className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm"
                >
                  <span className="font-semibold text-foreground">
                    {platformName(ch.platform)}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {compact(ch.followers)} · {pct(ch.engagementRate)}
                  </span>
                </div>
              ))}
            </div>
            {profile?.audience ? (
              <>
                <p className="pt-1 text-sm font-bold text-foreground">Audience ages</p>
                {profile.audience.ages.map((g) => (
                  <div key={g.bucket} className="flex items-center gap-2 text-xs">
                    <span className="w-12 text-muted-foreground">{g.bucket}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E5E7EB]">
                      <span
                        className="block h-full rounded-full bg-[#4D7C0F]"
                        style={{ width: `${Math.round(g.share * 100)}%` }}
                      />
                    </span>
                    <span className="w-9 text-right tabular-nums text-foreground">
                      {Math.round(g.share * 100)}%
                    </span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  {profile.audience.genders
                    .map((g) => `${Math.round(g.share * 100)}% ${g.label.toLowerCase()}`)
                    .join(' · ')}
                  {profile.audience.locations[0]
                    ? ` · ${Math.round(profile.audience.locations[0].share * 100)}% ${profile.audience.locations[0].name}`
                    : ''}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Audience details show once they connect an account.
              </p>
            )}
          </>
        ) : tab === 'content' ? (
          <>
            <p className="text-sm font-bold text-foreground">In the shop, in their order</p>
            <ol className="space-y-1.5">
              {a.themes.map((t, i) => (
                <li key={t.label} className="flex items-center gap-2 text-sm text-foreground">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-xs font-bold">
                    {i + 1}
                  </span>
                  {t.label}
                </li>
              ))}
            </ol>
            {profile?.featured?.length ? (
              <div className="grid grid-cols-2 gap-2 pt-1">
                {profile.featured.slice(0, 4).map((p: any) => (
                  <a
                    key={p.link}
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                    className="overflow-hidden rounded-lg border border-border"
                  >
                    <img src={p.thumbnail} alt="" className="aspect-square w-full object-cover" />
                    <p className="truncate px-2 py-1 text-xs text-muted-foreground">
                      {compact(p.views ?? 0)} views
                    </p>
                  </a>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className="text-sm text-foreground">
              <span className="font-bold">{profile?.record?.completed ?? a.completedDeals}</span>{' '}
              completed on Athios
              {profile?.record?.inProgress ? `, ${profile.record.inProgress} in progress` : ''}.
            </p>
            {profile?.record?.brands?.length ? (
              <div className="flex flex-wrap gap-2">
                {profile.record.brands.map((b: any) => (
                  <span
                    key={b.name}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-foreground"
                  >
                    {b.logo ? <img src={b.logo} alt="" className="h-5 w-5 rounded-full" /> : null}
                    {b.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No brand deals on Athios yet.</p>
            )}
          </>
        )}
        <Link
          to={`/athlete/${a.id}${campaignId ? `?campaignId=${campaignId}` : ''}`}
          className="block text-center text-sm font-semibold text-[#3F7A0A] underline-offset-2 hover:underline"
        >
          Open their Content Shop
        </Link>
      </div>
    </aside>
  )
}

/**
 * The brand's Marketplace: athletes ranked by how well they fit the chosen campaign, with
 * filters, a card per athlete (fit, stats, themes, why they match) and, on wide screens, the
 * selected athlete beside the grid.
 */
const Marketplace: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { campaignId: routeCampaign } = useParams()
  const [params, setParams] = useSearchParams()
  const { data: campaigns = [] } = useGetCampaignsQuery()
  const open = campaigns.filter((c) => !['completed', 'archived', 'draft'].includes(c.status))
  const campaignId =
    params.get('campaignId') ?? routeCampaign ?? (open[0]?.id ? String(open[0].id) : null)
  const campaign = campaigns.find((c) => String(c.id) === campaignId) ?? null

  // The intake's one-line read of what the brand is after, shown once on arrival
  const [welcome, setWelcome] = useState<string | null>(() => {
    if (params.get('welcome') !== '1') return null
    try {
      return sessionStorage.getItem('athios_intake_summary') ?? ''
    } catch {
      return ''
    }
  })
  const [search, setSearch] = useState(params.get('q') ?? '')
  const [q, setQ] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])
  const [view, setView] = useState<'recommended' | 'all'>('recommended')
  const [sport, setSport] = useState('')
  const [audience, setAudience] = useState('')
  const [state, setState] = useState('')
  const [theme, setTheme] = useState('')
  const [limit, setLimit] = useState(24)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [panelClosed, setPanelClosed] = useState(false)
  const [saved, setSaved] = useState<Record<string, boolean>>({})
  const [messageTo, setMessageTo] = useState<MarketplaceAthlete | null>(null)

  const { data, isFetching, isLoading } = useGetMarketplaceQuery({
    campaignId,
    q,
    sport,
    audience,
    state,
    theme,
    view,
    limit,
  })
  const [setShortlisted] = useSetShortlistedMutation()
  const [createDirectMessage, { isLoading: sendingMessage }] =
    useCreateDirectMessageThreadMutation()

  const athletes = data?.athletes ?? []
  const selected = athletes.find((a) => a.id === selectedId) ?? (panelClosed ? null : athletes[0])
  const target = data?.target
  const filtersOn = !!(sport || audience || state || theme || q)

  const chooseCampaign = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('campaignId', id)
    setParams(next, { replace: true })
    setSelectedId(null)
  }
  const offerHref = (a: MarketplaceAthlete) =>
    `/athlete/${a.id}?${new URLSearchParams({ ...(campaignId ? { campaignId } : {}), offer: '1' })}`
  const reportHref = (a: MarketplaceAthlete) =>
    `/athlete/${a.id}${campaignId ? `?campaignId=${campaignId}` : ''}`
  const isSaved = (a: MarketplaceAthlete) => saved[a.id] ?? a.saved
  const toggleSave = async (a: MarketplaceAthlete) => {
    const on = !isSaved(a)
    setSaved((s) => ({ ...s, [a.id]: on }))
    try {
      await setShortlisted({ id: a.id, on }).unwrap()
      toast({
        title: on
          ? `${a.name.split(' ')[0]} saved to your shortlist`
          : 'Removed from your shortlist',
      })
    } catch {
      setSaved((s) => ({ ...s, [a.id]: !on }))
    }
  }
  const sendMessage = async (d: DirectMessageData) => {
    if (!messageTo) return
    try {
      await createDirectMessage({ athleteId: messageTo.id, message: d.message }).unwrap()
      toast({ title: 'Message sent', description: `${messageTo.name} will see it in Messages.` })
      setMessageTo(null)
    } catch (err: any) {
      toast({
        title: "Couldn't send the message",
        description: err?.data?.error ?? 'Try again.',
        variant: 'destructive',
      })
    }
  }

  const themeOptions = useMemo(
    () => CONTENT_CATEGORIES.map((c) => ({ value: c.slug, label: c.name })),
    [],
  )

  // The banner: who this campaign is looking for
  const banner = [
    {
      icon: <Users className="h-5 w-5" />,
      label: 'Audience',
      value:
        [target?.ages.join(', ').replace(/-/g, '–'), target?.sports.slice(0, 3).join(', ')]
          .filter(Boolean)
          .join(' · ') || 'Any sport',
    },
    {
      icon: <Trophy className="h-5 w-5" />,
      label: 'Brand',
      value: target?.brandName ?? campaign?.brand?.name ?? 'Your brand',
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      label: 'Location',
      value: target?.states.length ? target.states.join(', ') : 'Anywhere',
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      label: 'Content style',
      value: target?.themes.join(', ') || target?.platforms.map(platformName).join(', ') || 'Any',
    },
  ]

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1480px] space-y-4 p-4 sm:p-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Shop athlete content
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            See what college athletes already make, then book it. Prices up front, you approve
            every piece, and the whole deal happens here.
          </p>
        </div>

        {/* Campaign, search, view */}
        <div className="grid gap-3 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_auto]">
          <Select value={campaignId ?? ''} onValueChange={chooseCampaign}>
            <SelectTrigger
              aria-label="Campaign to match for"
              className="h-14 min-w-0 rounded-xl border-border bg-card px-3 text-left [&>svg]:hidden"
            >
              <div className="flex w-full min-w-0 items-center gap-3">
                {campaign?.brand?.logo ? (
                  <img
                    src={campaign.brand.logo}
                    alt=""
                    className="h-9 w-9 rounded-lg object-cover"
                  />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#E8F9D2] text-[#3F7A0A]">
                    <Target className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <span className="block text-xs text-muted-foreground">Active campaign</span>
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {campaign?.name ?? (open.length ? 'Pick a campaign' : 'No campaigns yet')}
                  </span>
                </div>
                <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {open.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                  {c.brand?.name ? ` · ${c.brand.name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="relative block min-w-0">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content, athletes or schools…"
              aria-label="Search the Marketplace"
              className="h-14 w-full rounded-xl border border-border bg-card pl-11 pr-4 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
            />
          </label>
          <div className="grid h-14 grid-cols-2 gap-1 rounded-xl border border-border bg-card p-1">
            {(
              [
                ['recommended', 'Recommended'],
                ['all', 'All athletes'],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={cn(
                  'rounded-lg px-4 text-sm font-semibold',
                  view === v
                    ? 'bg-[#C0FD72] text-[#0F172A]'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <FilterSelect
            icon={<SlidersHorizontal className="h-4 w-4" />}
            label="Content"
            value={theme}
            onChange={setTheme}
            options={themeOptions}
          />
          <FilterSelect
            icon={<Trophy className="h-4 w-4" />}
            label="Sport"
            value={sport}
            onChange={setSport}
            options={SPORTS.map((s) => ({ value: s, label: s }))}
          />
          <FilterSelect
            icon={<Users className="h-4 w-4" />}
            label="Audience size"
            value={audience}
            onChange={setAudience}
            options={AUDIENCE_SIZES.map((s) => ({ value: s.id, label: s.label }))}
          />
          <FilterSelect
            icon={<MapPin className="h-4 w-4" />}
            label="Location"
            value={state}
            onChange={setState}
            options={STATES.map((s) => ({ value: s, label: s }))}
          />
          {filtersOn ? (
            <button
              type="button"
              onClick={() => {
                setSport('')
                setAudience('')
                setState('')
                setTheme('')
                setSearch('')
              }}
              className="col-span-2 h-11 px-2 text-sm font-semibold text-[#3F7A0A] underline-offset-2 hover:underline sm:col-span-1"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        <div
          className={cn(
            'grid gap-4',
            selected && !panelClosed ? 'xl:grid-cols-[minmax(0,1fr)_380px]' : '',
          )}
        >
          <div className="min-w-0 space-y-4">
            {/* Just arrived from the intake: what we understood, and what to do next */}
            {welcome ? (
              <section className="flex items-start gap-3 rounded-2xl border border-[#BFD7FB] bg-[#EFF6FF] p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#DBEAFE] text-[#1D4ED8]">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-foreground">Here are your athletes</p>
                  <p className="text-sm text-foreground">
                    {welcome} Open anyone to see why they fit, their rates and their content, then
                    tap <b>Work with</b> to send an offer. Your campaign is a draft only you can
                    see.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setWelcome(null)
                    const nextParams = new URLSearchParams(params)
                    nextParams.delete('welcome')
                    setParams(nextParams, { replace: true })
                  }}
                  aria-label="Dismiss"
                  className="shrink-0 rounded-lg p-1 text-muted-foreground hover:bg-[#DBEAFE]"
                >
                  <X className="h-4 w-4" />
                </button>
              </section>
            ) : null}

            {/* Who the campaign is looking for */}
            {view === 'recommended' ? (
              <section className="grid gap-4 rounded-2xl border border-[#C9E9A6] bg-[#F7FEE7] p-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)]">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#C0FD72] text-[#2F5E06]">
                    <Target className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-bold text-foreground">
                      Top matches for {target?.campaignName ?? 'your brand'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Ranked by how well each athlete fits who this campaign is for.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {banner.map((b) => (
                    <div key={b.label} className="min-w-0 text-center">
                      <span className="mx-auto flex justify-center text-[#3F7A0A]">{b.icon}</span>
                      <p className="mt-1 text-xs font-bold text-foreground">{b.label}</p>
                      <p className="line-clamp-2 text-xs text-muted-foreground">{b.value}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {isLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-[520px] rounded-2xl" />
                ))}
              </div>
            ) : !athletes.length ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <p className="font-semibold text-foreground">No athletes match these filters</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Try a wider audience size or another state.
                </p>
              </div>
            ) : (
              <div
                className={cn(
                  'grid gap-4 sm:grid-cols-2',
                  selected && !panelClosed ? '2xl:grid-cols-3' : 'xl:grid-cols-3 2xl:grid-cols-4',
                  isFetching && 'opacity-60',
                )}
              >
                {athletes.map((a) => (
                  <AthleteMarketCard
                    key={a.id}
                    a={a}
                    selected={selected?.id === a.id && !panelClosed}
                    saved={isSaved(a)}
                    onSelect={() => {
                      // Phones and tablets have no side panel: open the full report
                      if (window.innerWidth < 1280) navigate(reportHref(a))
                      else {
                        setSelectedId(a.id)
                        setPanelClosed(false)
                      }
                    }}
                    onSave={() => toggleSave(a)}
                    offerHref={offerHref(a)}
                    reportHref={reportHref(a)}
                  />
                ))}
              </div>
            )}
            {data?.hasMore ? (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + 24)}
                  className="h-11 rounded-xl border border-border bg-card px-6 text-sm font-semibold text-foreground hover:bg-muted"
                >
                  Show more athletes
                </button>
              </div>
            ) : null}
          </div>

          {selected && !panelClosed ? (
            <div className="hidden xl:block">
              <AthletePanel
                a={selected}
                campaignId={campaignId}
                offerHref={offerHref(selected)}
                onMessage={() => setMessageTo(selected)}
                onClose={() => setPanelClosed(true)}
              />
            </div>
          ) : null}
        </div>
      </div>

      <SendMessageModal
        open={!!messageTo}
        onOpenChange={(o) => !o && setMessageTo(null)}
        athlete={messageTo as any}
        onSubmit={sendMessage}
        isSending={sendingMessage}
      />
    </MainLayout>
  )
}

export default Marketplace
